import styles from "./SldpPlayer.module.scss"

import React, { memo, useEffect, useRef } from "react"

import { useLayerPopupContext, useSchedule } from "@shared/contexts"
import { getParam } from "@shared/modules"
import { playerService } from "@shared/services"

import {
  useSetPlayerIsRefreshing,
  useSetPlayerRefreshIsRemounted,
} from "live/contexts"
import { createSldpPlayer, Player } from "live/modules"
import { PlayerStatus } from "live/types"

const ERROR_RESTART_DELAY = 1000 * 5
const INIT_DELAY = 1000 * 3
const REPLAY_DELAY = 1000 * 7

interface Props {
  player: Player | undefined
  setPlayer: React.Dispatch<React.SetStateAction<Player | undefined>>
  setStatus: React.Dispatch<React.SetStateAction<PlayerStatus>>
  handleFullMode: Function
}

function SldpPlayer({ player, setPlayer, setStatus, handleFullMode }: Props) {
  const { video } = useLayerPopupContext()
  const { schedule } = useSchedule()

  const replayDelayRef = useRef<number>(INIT_DELAY)
  const playerRef = useRef<HTMLVideoElement>(null)
  const setIsPlayerRemounted = useSetPlayerRefreshIsRemounted()
  const setIsPlayerRefresing = useSetPlayerIsRefreshing()

  const isOnlyPlayerMode = getParam("is_only_player") === "true"
  const streamingUrl = schedule?.streamingUrl
  const streamingStatus = schedule?.streamingStatus

  useEffect(() => {
    if (playerRef.current && !player) {
      setPlayer({
        ...createSldpPlayer({
          video_element: playerRef.current!.id,
        }),
        requestFullscreen: handleFullMode,
      })
    }

    return () => {
      player?.destroy?.()
    }
  }, [player, setPlayer, handleFullMode])

  useEffect(() => {
    if (!player || !streamingUrl) return

    if (streamingStatus === "WAITING") {
      setStatus("ERROR")
      return
    }

    if (streamingStatus === "END") {
      setStatus("ERROR")
      player.stop()
      return
    }

    setTimeout(async () => {
      player.setStreamURL(
        await playerService.getStreamingUrl(streamingUrl, "LIVE"),
      )
      setIsPlayerRefresing(false)
    }, replayDelayRef.current)
    replayDelayRef.current = REPLAY_DELAY
  }, [streamingUrl, streamingStatus, player, setStatus, setIsPlayerRefresing])

  useEffect(() => {
    if (!player) return

    player.setCallbacks({
      onError: () => {
        setStatus("ERROR")
        setTimeout(() => {
          player.play()
        }, ERROR_RESTART_DELAY)
      },
      onConnectionEstablished: () => {
        if (playerRef.current?.paused) {
          setStatus("PAUSE")
        }
      },
    })
  }, [player, setStatus])

  useEffect(() => {
    const video = playerRef.current!

    function evtPause() {
      setStatus("PAUSE")
      player?.pause()
    }

    function evtPlay() {
      setStatus("LOADING")
      player?.play()
    }

    const evtPlaying = () => {
      const stream = player?.getCurrentStream?.()

      if (player && !stream) {
        setIsPlayerRemounted(true)
        return
      }

      setStatus("PLAYING")
    }

    const evtEnded = () => {
      setStatus("ERROR")
    }

    video.addEventListener("pause", evtPause)
    video.addEventListener("play", evtPlay)
    video.addEventListener("playing", evtPlaying)
    video.addEventListener("ended", evtEnded)

    return () => {
      video.removeEventListener("pause", evtPause)
      video.removeEventListener("play", evtPlay)
      video.removeEventListener("playing", evtPlaying)
      video.removeEventListener("ended", evtEnded)
    }
  }, [playerRef, setStatus, player, setIsPlayerRemounted])

  return (
    <video
      id="video"
      className={`${styles.player} ${styles[video.size]} ${styles.subStreamer}`}
      ref={playerRef}
      controls={isOnlyPlayerMode}
      controlsList="nofullscreen nodownload noplaybackrate noremoteplayback"
    />
  )
}

export default memo(SldpPlayer)
